import { Coins, Filter, Plus, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { apiUrl } from '@/config/api';

const API_BASE = apiUrl('/api/fees');

interface FeeItem {
  id: string;
  code: string;
  name: string;
  type: 'EXAM_FEE' | 'ADMISSION_FEE' | 'TUITION' | 'FACILITY_FEE' | 'OTHER';
  amount: number;
  applicantCategory: string;
  catalogId?: string;
  isActive: boolean;
  updatedAt: string;
}

export default function BillingItemList() {
  const [fees, setFees] = useState<FeeItem[]>([]);
  const [loading, setLoading] = useState(true);

  // Example filter state
  const [selectedCatalogId, setSelectedCatalogId] = useState<string>('');

  useEffect(() => {
    const fetchFees = async () => {
      setLoading(true);
      try {
        const query = new URLSearchParams();
        if (selectedCatalogId) query.append('catalogId', selectedCatalogId);

        const res = await fetch(`${API_BASE}?${query.toString()}`);
        if (res.ok) {
          const data = (await res.json()) as FeeItem[]; // Actual API response is array based on fees.ts line 66
          setFees(data);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchFees();
  }, [selectedCatalogId]);

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-8">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900 flex items-center gap-2">
            <Coins className="w-8 h-8 text-yellow-600" />
            Billing Items (Fees)
          </h1>
          <p className="text-gray-500 mt-1">入試検定料、入学金、学費等の品目マスター管理</p>
        </div>
        <Button className="gap-2">
          <Plus className="w-4 h-4" />
          Create Fee Item
        </Button>
      </div>

      <div className="flex gap-4 items-center bg-gray-50 p-4 rounded-lg border">
        <Filter className="w-4 h-4 text-gray-500" />
        <span className="text-sm font-medium">Filter by Catalog:</span>
        <select
          className="border rounded px-2 py-1 text-sm"
          value={selectedCatalogId}
          onChange={(e) => setSelectedCatalogId(e.target.value)}
        >
          <option value="">All Catalogs</option>
          {/* Ideally populated from CatalogList */}
          <option value="2026-v1">2026-v1 (Draft)</option>
          <option value="2025-v3">2025-v3 (Published)</option>
        </select>
        <div className="ml-auto relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
          <Input placeholder="Search items..." className="pl-9 w-[250px] bg-white" />
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Category</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {/* Mock Row if empty */}
              {!loading && fees.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                    No billing items found.
                  </TableCell>
                </TableRow>
              )}
              {fees.map((fee) => (
                <TableRow key={fee.id}>
                  <TableCell className="font-mono">{fee.code}</TableCell>
                  <TableCell className="font-medium">{fee.name}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{fee.type}</Badge>
                  </TableCell>
                  <TableCell>{fee.applicantCategory}</TableCell>
                  <TableCell className="text-right font-bold">¥{fee.amount.toLocaleString()}</TableCell>
                  <TableCell>
                    {fee.isActive ? (
                      <span className="flex items-center gap-1 text-green-600 text-xs font-bold">
                        <span className="w-2 h-2 rounded-full bg-green-600" />
                        Active
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-gray-400 text-xs text-xs font-bold">
                        <span className="w-2 h-2 rounded-full bg-gray-400" />
                        Inactive
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm">
                      Edit
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
